import type { RefCallback } from 'react';
import { useCallback, useEffect, useState } from 'react';

interface UsePersistentIntersectionObserverArgs extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

type UsePersistentIntersectionObserverResult<T extends Element> = [
  ref: RefCallback<T>,
  entry: IntersectionObserverEntry | undefined,
];

/**
 * Drop-in replacement for `~/hooks/useIntersectionObserver` that survives the observed
 * element being mounted *after* the hook first runs.
 *
 * Why this exists:
 *   The shared `useIntersectionObserver` accepts a `RefObject` and lists it in the effect's
 *   deps. The ref *object* is stable across renders (that's the whole point of `useRef`),
 *   but `ref.current` is not — and `current` isn't a render-tracked value, so the effect
 *   never re-runs when the node it points to changes. If the target element is rendered
 *   conditionally (e.g. the infinite-scroll sentinel only appears once items load, or the
 *   list is wrapped in an `isLoading` skeleton branch), the effect runs on first commit
 *   with `current === null`, returns early, and never re-attaches when the real node
 *   shows up. Result: the IntersectionObserver is never created, and the consumer
 *   silently loses callbacks (infinite scroll stops loading next pages, lazy images
 *   never fire, etc.).
 *
 * The fix:
 *   Switch to a callback ref. React invokes the callback whenever the node it's attached
 *   to mounts or unmounts, and we store the node in `useState`. State changes are
 *   render-tracked, so the effect's deps include the actual node and the observer is
 *   (re)attached each time the target swaps in or out.
 *
 * When to prefer this over `useIntersectionObserver`:
 *   - The observed element is rendered conditionally or behind a loading branch.
 *   - The element can mount/unmount during the component's lifetime (errors, retries,
 *     virtualization, lazy hydration).
 *
 * Usage:
 *   const [ ref, entry ] = usePersistentIntersectionObserver({ rootMargin: '400px' });
 *   return <div ref={ref} />;
 */
export function usePersistentIntersectionObserver<T extends Element = Element>({
  threshold = 0,
  root = null,
  rootMargin = '0%',
  freezeOnceVisible = false,
}: UsePersistentIntersectionObserverArgs = {}): UsePersistentIntersectionObserverResult<T> {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback<RefCallback<T>>((el) => setNode(el), []);

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) return;
    if (frozen || !node) return;

    const observer = new IntersectionObserver(
      ([newEntry]) => {
        if (newEntry) setEntry(newEntry);
      },
      { threshold, root, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, JSON.stringify(threshold), root, rootMargin, frozen]);

  return [ref, entry];
}
